import Image from 'next/image';
import { oneLine } from 'common-tags';

import { RichInline } from '@/atoms';
import { Prose } from '@/molecules';

import type { CSSProperties, FunctionComponent } from 'react';
import type { TypeFeatureCardProps } from './types';

/** Tarjeta boxed (sin CTA por diseño). Usada en `ComponentRevisadasTarjetasDestacadas`. */
export const FeatureCard: FunctionComponent<TypeFeatureCardProps> = ({
  className = '',
  image,
  imageShapeUrl,
  title,
  description,
  titleStyleClass,
  descriptionStyleClass,
}: TypeFeatureCardProps) => {
  const maskStyle: CSSProperties | undefined = imageShapeUrl
    ? {
        WebkitMaskImage: `url(${imageShapeUrl})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: '100% 100%',
        maskImage: `url(${imageShapeUrl})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: '100% 100%',
      }
    : undefined;

  return (
    <article
      className={`bg-neutrals-0 shadow-light-s flex h-full flex-col items-center gap-6 rounded-[32px] px-4 py-8 ${className}`}
    >
      {image?.src && (
        <div
          className="relative aspect-[380/300] w-full max-w-[300px] overflow-hidden"
          style={maskStyle}
        >
          <Image
            src={image.src}
            alt={image.alt ?? title ?? ''}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover"
          />
        </div>
      )}

      {(title || description) && (
        <div className="flex flex-col items-center gap-2 text-center">
          {title && (
            <RichInline
              as="h3"
              html={title}
              className={oneLine`text-headline text-primary-500 font-primary ${
                titleStyleClass ?? 'text-headline-h3 font-bold'
              }`}
            />
          )}
          {description && (
            <Prose
              className={oneLine`text-body text-text-primary ${
                descriptionStyleClass ?? '[&_p]:text-body-l'
              }`}
              html={description}
            />
          )}
        </div>
      )}
    </article>
  );
};

FeatureCard.displayName = 'FeatureCard';
